import type { PixelData } from '@vikings/types';

/**
 * Render pixel data onto a canvas context.
 * Uses integer pixel sizes for sharp rendering.
 */
export function renderPixels(
  ctx: CanvasRenderingContext2D,
  data: PixelData,
  x: number,
  y: number,
  scale: number,
): void {
  for (let py = 0; py < data.height; py++) {
    for (let px = 0; px < data.width; px++) {
      const color = data.pixels[py]?.[px];
      if (color) {
        ctx.fillStyle = color;
        ctx.fillRect(
          Math.floor(x + px * scale),
          Math.floor(y + py * scale),
          Math.ceil(scale),
          Math.ceil(scale),
        );
      }
    }
  }
}

/**
 * Create an offscreen canvas with pre-rendered pixel data.
 * Returns the canvas element for fast blitting.
 */
export function createPixelSprite(
  data: PixelData,
  scale: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = data.width * scale;
  canvas.height = data.height * scale;

  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  renderPixels(ctx, data, 0, 0, scale);

  return canvas;
}

/**
 * Setup a canvas for pixel-perfect rendering.
 */
export function setupPixelCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  // CSS for sharp pixels
  canvas.style.imageRendering = 'pixelated';

  return ctx;
}

/**
 * Clear the canvas with a solid color.
 */
export function clearCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  color: string = '#1a1a2e',
): void {
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Render pixel-art text character by character.
 * Uses a built-in tiny pixel font.
 */
export function renderPixelText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  scale: number = 1,
  color: string = '#ffffff',
): void {
  ctx.fillStyle = color;
  const charWidth = 4 * scale;

  for (let i = 0; i < text.length; i++) {
    const char = text[i]!;
    const charData = PIXEL_FONT[char.toUpperCase()];
    if (!charData) {
      // Space or unknown character
      continue;
    }

    for (let cy = 0; cy < charData.length; cy++) {
      const row = charData[cy]!;
      for (let cx = 0; cx < row.length; cx++) {
        if (row[cx]) {
          ctx.fillRect(
            Math.floor(x + i * (charWidth + scale) + cx * scale),
            Math.floor(y + cy * scale),
            Math.ceil(scale),
            Math.ceil(scale),
          );
        }
      }
    }
  }

  // Return dimensions for layout purposes
  return;
}

/**
 * Calculate the width of rendered pixel text.
 */
export function measurePixelText(text: string, scale: number = 1): number {
  const charWidth = 4 * scale;
  const spacing = scale;
  return text.length * (charWidth + spacing) - spacing;
}

/**
 * Calculate the height of rendered pixel text.
 */
export function pixelTextHeight(scale: number = 1): number {
  return 5 * scale;
}

// ═══════════════════════════════════════════════
// Built-in 3x5 pixel font
// ═══════════════════════════════════════════════

type FontGlyph = number[][];

const PIXEL_FONT: Record<string, FontGlyph> = {
  A: [
    [0, 1, 0],
    [1, 0, 1],
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
  B: [
    [1, 1, 0],
    [1, 0, 1],
    [1, 1, 0],
    [1, 0, 1],
    [1, 1, 0],
  ],
  C: [
    [0, 1, 1],
    [1, 0, 0],
    [1, 0, 0],
    [1, 0, 0],
    [0, 1, 1],
  ],
  D: [
    [1, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 0],
  ],
  E: [
    [1, 1, 1],
    [1, 0, 0],
    [1, 1, 0],
    [1, 0, 0],
    [1, 1, 1],
  ],
  F: [
    [1, 1, 1],
    [1, 0, 0],
    [1, 1, 0],
    [1, 0, 0],
    [1, 0, 0],
  ],
  G: [
    [0, 1, 1],
    [1, 0, 0],
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 1],
  ],
  H: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
  I: [
    [1, 1, 1],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [1, 1, 1],
  ],
  J: [
    [0, 0, 1],
    [0, 0, 1],
    [0, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
  ],
  K: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
  ],
  L: [
    [1, 0, 0],
    [1, 0, 0],
    [1, 0, 0],
    [1, 0, 0],
    [1, 1, 1],
  ],
  M: [
    [1, 0, 1],
    [1, 1, 1],
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
  N: [
    [1, 0, 1],
    [1, 1, 1],
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
  O: [
    [0, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
  ],
  P: [
    [1, 1, 0],
    [1, 0, 1],
    [1, 1, 0],
    [1, 0, 0],
    [1, 0, 0],
  ],
  Q: [
    [0, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [0, 1, 1],
  ],
  R: [
    [1, 1, 0],
    [1, 0, 1],
    [1, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
  ],
  S: [
    [0, 1, 1],
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
    [1, 1, 0],
  ],
  T: [
    [1, 1, 1],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
  ],
  U: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
  ],
  V: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
    [0, 1, 0],
  ],
  W: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [1, 1, 1],
    [1, 0, 1],
  ],
  X: [
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
  ],
  Y: [
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
  ],
  Z: [
    [1, 1, 1],
    [0, 0, 1],
    [0, 1, 0],
    [1, 0, 0],
    [1, 1, 1],
  ],
  '0': [
    [0, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
  ],
  '1': [
    [0, 1, 0],
    [1, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [1, 1, 1],
  ],
  '2': [
    [1, 1, 0],
    [0, 0, 1],
    [0, 1, 0],
    [1, 0, 0],
    [1, 1, 1],
  ],
  '3': [
    [1, 1, 0],
    [0, 0, 1],
    [0, 1, 0],
    [0, 0, 1],
    [1, 1, 0],
  ],
  '4': [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [0, 0, 1],
    [0, 0, 1],
  ],
  '5': [
    [1, 1, 1],
    [1, 0, 0],
    [1, 1, 0],
    [0, 0, 1],
    [1, 1, 0],
  ],
  '6': [
    [0, 1, 1],
    [1, 0, 0],
    [1, 1, 0],
    [1, 0, 1],
    [0, 1, 0],
  ],
  '7': [
    [1, 1, 1],
    [0, 0, 1],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
  ],
  '8': [
    [0, 1, 0],
    [1, 0, 1],
    [0, 1, 0],
    [1, 0, 1],
    [0, 1, 0],
  ],
  '9': [
    [0, 1, 0],
    [1, 0, 1],
    [0, 1, 1],
    [0, 0, 1],
    [1, 1, 0],
  ],
  ',': [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
    [0, 1, 0],
    [1, 0, 0],
  ],
  '.': [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
    [0, 1, 0],
  ],
  ':': [
    [0, 0, 0],
    [0, 1, 0],
    [0, 0, 0],
    [0, 1, 0],
    [0, 0, 0],
  ],
  '!': [
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [0, 0, 0],
    [0, 1, 0],
  ],
  '?': [
    [1, 1, 0],
    [0, 0, 1],
    [0, 1, 0],
    [0, 0, 0],
    [0, 1, 0],
  ],
  '-': [
    [0, 0, 0],
    [0, 0, 0],
    [1, 1, 1],
    [0, 0, 0],
    [0, 0, 0],
  ],
  '+': [
    [0, 0, 0],
    [0, 1, 0],
    [1, 1, 1],
    [0, 1, 0],
    [0, 0, 0],
  ],
  ' ': [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ],
  '|': [
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
  ],
  '/': [
    [0, 0, 1],
    [0, 0, 1],
    [0, 1, 0],
    [1, 0, 0],
    [1, 0, 0],
  ],
  "'": [
    [0, 1, 0],
    [0, 1, 0],
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ],
  '’': [
    [0, 1, 0],
    [0, 1, 0],
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ],
  Б: [
    [1, 1, 1],
    [1, 0, 0],
    [1, 1, 0],
    [1, 0, 1],
    [1, 1, 0],
  ],
  Г: [
    [1, 1, 1],
    [1, 0, 0],
    [1, 0, 0],
    [1, 0, 0],
    [1, 0, 0],
  ],
  Ґ: [
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 0],
    [1, 0, 0],
    [1, 0, 0],
  ],
  Д: [
    [0, 1, 0],
    [0, 1, 0],
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
  Є: [
    [0, 1, 1],
    [1, 0, 0],
    [1, 1, 0],
    [1, 0, 0],
    [0, 1, 1],
  ],
  Ж: [
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
  ],
  З: [
    [1, 1, 0],
    [0, 0, 1],
    [0, 1, 0],
    [0, 0, 1],
    [1, 1, 0],
  ],
  И: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
  ],
  І: [
    [1, 1, 1],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [1, 1, 1],
  ],
  Ї: [
    [1, 0, 1],
    [0, 0, 0],
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
  ],
  Й: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 0],
    [1, 0, 1],
    [1, 0, 1],
  ],
  Л: [
    [0, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
  П: [
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
  У: [
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 0],
    [0, 1, 0],
    [1, 0, 0],
  ],
  Ф: [
    [0, 1, 0],
    [1, 1, 1],
    [1, 1, 1],
    [0, 1, 0],
    [0, 1, 0],
  ],
  Ц: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [0, 0, 1],
  ],
  Ч: [
    [1, 0, 1],
    [1, 0, 1],
    [0, 1, 1],
    [0, 0, 1],
    [0, 0, 1],
  ],
  Ш: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [1, 1, 1],
  ],
  Щ: [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [1, 1, 1],
    [0, 0, 1],
  ],
  Ь: [
    [1, 0, 0],
    [1, 0, 0],
    [1, 1, 0],
    [1, 0, 1],
    [1, 1, 0],
  ],
  Ю: [
    [1, 0, 1],
    [1, 1, 1],
    [1, 1, 1],
    [1, 1, 1],
    [1, 0, 1],
  ],
  Я: [
    [0, 1, 1],
    [1, 0, 1],
    [0, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
  ],
};

// Aliases for Cyrillic letters that share Latin glyphs
PIXEL_FONT['А'] = PIXEL_FONT['A']!;
PIXEL_FONT['В'] = PIXEL_FONT['B']!;
PIXEL_FONT['Е'] = PIXEL_FONT['E']!;
PIXEL_FONT['К'] = PIXEL_FONT['K']!;
PIXEL_FONT['М'] = PIXEL_FONT['M']!;
PIXEL_FONT['Н'] = PIXEL_FONT['H']!;
PIXEL_FONT['О'] = PIXEL_FONT['O']!;
PIXEL_FONT['Р'] = PIXEL_FONT['P']!;
PIXEL_FONT['С'] = PIXEL_FONT['C']!;
PIXEL_FONT['Т'] = PIXEL_FONT['T']!;
PIXEL_FONT['Х'] = PIXEL_FONT['X']!;
